<script lang="ts" setup>
const emit = defineEmits<{
  (event: 'mouseenter'): void;
  (event: 'mouseleave'): void;
  (event: 'focus'): void;
  (event: 'blur'): void;
  (event: 'update:state', value: boolean | null): void;
  (event: 'check'): void;
  (event: 'uncheck'): void;
  (event: 'indeterminate'): void;
}>();

const props = withDefaults(
  defineProps<{
    state?: boolean | null | undefined;
    disabled?: boolean;
    mixed?: boolean;
    variant?: 'circle' | 'square';
  }>(),
  {
    state: undefined,
    disabled: false,
    mixed: true,
    variant: 'square',
  },
);

// TODO aria- attributes (label, labelledby, controls, ...)
const iconAttrs = computed(() => ({
  class: ['tw-checkbox--icon'],
}));

const internalState = ref<boolean | null>(typeof props.state !== 'undefined' ? props.state : false);
const state = computed({
  get: () => (typeof props.state === 'undefined' ? internalState.value : props.state),
  set: (v: boolean | null) => {
    if (props.disabled) return;
    internalState.value = v;

    emit('update:state', internalState.value);
    if (internalState.value === true) emit('check');
    else if (internalState.value === null) emit('indeterminate');
    else emit('uncheck');
  },
});

const stateClass = computed(() => (state.value === true ? 'is--checked' : state.value === false ? 'is--unchecked' : 'is--indeterminate'));

const check = () => (state.value = true);
const uncheck = () => (state.value = false);
const indeterminate = () => (state.value = null);
const toggle = () => (state.value = !state.value);

const { events: hoverEvents, class: hoverClass } = useHover(emit, props);
const { events: focusEvents, class: focusClass } = useFocus(emit, props);
</script>

<template>
  <label
    :aria-checked="state === null ? 'mixed' : state"
    :aria-disabled="props.disabled"
    :class="['tw-checkbox', props.mixed ? 'is--3state' : 'is--2state', stateClass, props.disabled && 'is--disabled', hoverClass, focusClass]"
    :tabindex="props.disabled ? -1 : 0"
    role="checkbox"
    @click="state = !state"
    v-on="{ ...hoverEvents, ...focusEvents }"
    @keydown.enter.space.prevent="state = !state"
  >
    <slot :attrs="iconAttrs" :check="check" :indeterminate="indeterminate" :state="state" :toggle="toggle" :uncheck="uncheck" name="icon">
      <Icon :name="variant === 'circle' ? 'bi:check-circle-fill' : 'bi:check-square-fill'" v-if="state === true" class="icon" v-bind="iconAttrs" />
      <Icon :name="variant === 'circle' ? 'bi:dash-circle-fill' : 'bi:dash-square-fill'" v-else-if="state === null" class="icon" v-bind="iconAttrs" />
      <template v-else>
        <Icon name="bi:circle" v-if="variant === 'circle'" class="icon" v-bind="iconAttrs" />
        <Icon name="bi:square" v-else v-bind="iconAttrs" class="icon" />
      </template>
    </slot>

    <slot :check="check" :indeterminate="indeterminate" :state="state" :toggle="toggle" :uncheck="uncheck" />
  </label>
</template>

<style lang="scss" scoped>
@import '~/assets/styles/tw-variables.scss';
@import '~/assets/styles/tw-mixins.scss';

.tw-checkbox {
  display: inline-flex;
  align-items: stretch;
  gap: $tw-component--gap;
  padding: $tw-component--gap;
  border: none;
  transition: color, background-color, border-color, text-decoration-color;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;
  user-select: none;
  @include tw-color-set(inherit, transparent);

  &.is--checked {
    :slotted(.tw-checkbox--icon) {
      @include tw-color-set($primary);
    }
  }

  &.is--indeterminate.is--2state,
  &.is--unchecked {
    :slotted(.tw-checkbox--icon) {
      @include tw-color-set($black);
    }
  }

  &.is--indeterminate.is--3state {
    :slotted(.tw-checkbox--icon) {
      @include tw-color-set($primary);
    }
  }

  &.is--disabled {
    @include tw-color-set($gray);

    :slotted(.tw-checkbox--icon) {
      @include tw-color-set(inherit);
    }
  }
}

:slotted(.tw-checkbox--icon) {
  flex: 0 0 1em;
  min-width: 1em;
  align-self: flex-start;
  margin-top: $tw-component--gap; // manually set vertical-align to baseline
}

.icon {
  width: 1.2rem;
  height: 1.2rem;
}
</style>
